import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { PghUserDeviceBase } from '../../../../shared/device/pgh-user-device';
import { EquipmentInfoDto, SerialSearchResponseDto } from '../../../../shared/serial-search/serial-search-response.dto';
import { buildDeviceDisplayName } from '../../../../shared/device/device-display-name.pipe';
import { SelectionPoint } from '../../../../shared/array-object-selector/selection-point';
import { ADPResourceService, CustomFormControl } from 'digital-core';
import { isEmpty as _isEmpty } from 'lodash';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DeviceDto } from '../../../../core/services/device-inventory/device.dto';
import { HttpErrorResponse } from '@angular/common/http';
import { format } from 'date-fns';

export interface DeviceInfoFormResult {
  equipmentInfo: EquipmentInfoDto;
  deviceNickname: string;
}

@Component({
  selector: 'pgh-cellular-device-info-form',
  templateUrl: './cellular-device-info-form.component.html',
  styleUrls: ['./cellular-device-info-form.component.scss']
})
export class CellularDeviceInfoFormComponent implements OnInit, OnDestroy {

  @Input() public preHeaderTextSfKey?: string;
  @Input() public headerTextSfKey?: string;
  @Input() public bodyTextSfKey?: string;
  @Input() public submitButtonLabelSfKey?: string;

  /**
   * This is the device the user had already selected in a previous step in
   * whatever flow they are in. There are some flows where the user won't be
   * selecting a device before seeing this component so null/undefined are
   * valid and supported values.
   */
  @Input() public preSelectedDevice?: PghUserDeviceBase;

  @Input() public devicesFromAllInventories?: DeviceDto[];

  @Input() public serialSearchResponse?: SerialSearchResponseDto;

  @Input() public selectionPoints?: SelectionPoint[];

  @Output() public formSubmitted = new EventEmitter<DeviceInfoFormResult>();

  public preHeaderTextContext?: { device: string };

  public deviceInfoForm = new FormGroup({
    selectedDevice: new FormControl<EquipmentInfoDto>(undefined, [Validators.required]),
    nickname: new CustomFormControl('', (control) => this.validateNicknameIsUnique(control)),
  });

  public constructor(
    public resourceService: ADPResourceService,
  ) { }

  public ngOnInit(): void {
    this.preHeaderTextContext = { device: buildDeviceDisplayName(this.preSelectedDevice) };

    this.selectionPoints = this.selectionPoints ?? this.buildSelectionPoints();
  }

  public ngOnDestroy(): void {
    this.formSubmitted.complete();    
  }

  public handleDeviceSelectionChanged(newlySelectedDevice: EquipmentInfoDto) {
    this.deviceInfoForm.controls.selectedDevice.setValue(newlySelectedDevice);
    this.setNicknameField();
  }

  public handleFormSubmitted() {
    if (this.deviceInfoForm.invalid) {
      return;
    }

    this.formSubmitted.emit({
      equipmentInfo: this.deviceInfoForm.controls.selectedDevice.value,
      deviceNickname: this.deviceInfoForm.controls.nickname.value,
    });
  }

  public handleMultipleObjectsDetected(objectsDetected: EquipmentInfoDto[]) {
    console.error("There were multiple device options and the user has selected all the values we asked them. This is not expected; we expected to drill down to only one device option. Here are the device options that were left after the user made their selection:", objectsDetected);

    throw new HttpErrorResponse({
      error: {
        ErrorId: `PGH-UI-${format(new Date(), 'yyyyMMdd-HHmmssSSS')}`,
        Source: 'CellularDeviceInfoFormComponent_handleMultipleObjectsDetected',
        ErrorCode: 'Multiple_Device_Options_Detected',
      }
    });
  }

  private setNicknameField() {
    if (!_isEmpty(this.preSelectedDevice?.nickName?.trim())) {
      this.deviceInfoForm.controls.nickname.setValue(this.preSelectedDevice?.nickName?.trim());
      this.deviceInfoForm.controls.nickname.markAsTouched();
      return;
    }

    const selectedDevice = this.deviceInfoForm.controls.selectedDevice.value;
    if (selectedDevice == null) {
      return;
    }

    let nickname = `${selectedDevice.Manufacturer ?? ''} ${selectedDevice.MarketingName ?? ''}`.trim();
    let counter = 1;

    while (!this.isNicknameUnique(nickname)) {
      counter++;
      nickname = `${selectedDevice.Manufacturer ?? ''} ${selectedDevice.MarketingName ?? ''} ${counter}`.trim();
    }

    this.deviceInfoForm.controls.nickname.setValue(nickname);
    this.deviceInfoForm.controls.nickname.markAsTouched();
  }

  private buildSelectionPoints(): SelectionPoint[] {
    return [
      {
        propertyPath: 'Manufacturer',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_MakeField_Label'),
      },
      {
        propertyPath: 'MarketingName',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_ModelField_Label'),
      },
      {
        propertyPath: 'Attributes.StorageSize',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_StorageCapacityField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_StorageCapacityField_ValueToDisplayWhenNullOption'),
      },
      {
        propertyPath: 'Attributes.Color',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_ColorField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_ColorField_ValueToDisplayWhenNullOption'),
      },
      {
        propertyPath: 'Attributes.Material',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_MaterialField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_MaterialField_ValueToDisplayWhenNullOption'),
      },
      {
        propertyPath: 'Attributes.Carrier',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_CarrierField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_CarrierField_ValueToDisplayWhenNullOption'),
      },
    ];
  }

  private isNicknameUnique(nickname: string) {
    return !(this.devicesFromAllInventories?.map((device) => device.nickName?.toLowerCase()) ?? []).includes(nickname.toLowerCase());
  }

  private validateNicknameIsUnique(control: AbstractControl<any, any>): ValidationErrors {
    if (control?.value != null) {
      return this.isNicknameUnique(control.value?.trim()) ? null : { uniqueNickname: true };
    }

    return null;
  }

}
